import { useTranslation } from 'react-i18next';
import { compareGrid } from './CompareOverlay';

const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_BODY = "'DM Sans', sans-serif";

// One card per compared item, every card listing the same rows in the same order so values line
// up side by side on desktop and read top-to-bottom per item on mobile (the grid wraps to one
// column). A row's render() returning null/undefined/'' shows an em dash, so a missing value is
// visibly missing rather than silently dropping the row and misaligning the cards.
export default function CompareCards({ items, rows, loading, onRemove }) {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>
        {t('compare.empty')}
      </div>
    );
  }

  return (
    <>
      {items.length === 1 && (
        <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)', marginBottom: '14px' }}>
          {t('compare.addAnotherHint')}
        </p>
      )}
      <div style={compareGrid} data-testid="compare-grid">
        {items.map(item => (
          <div
            key={item.id}
            data-testid="compare-card"
            style={{ background: NAVY_MID, border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '14px 16px', minWidth: 0 }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', marginBottom: '12px' }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: '0.95rem', fontWeight: 700, color: WHITE, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{item.title}</p>
                {item.subtitle && (
                  <p style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.45)', marginTop: '2px', overflowWrap: 'anywhere' }}>{item.subtitle}</p>
                )}
              </div>
              {onRemove && (
                <button
                  onClick={() => onRemove(item.id)}
                  aria-label={t('compare.removeAriaLabel', { name: item.title })}
                  style={{
                    background: 'none', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px',
                    color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem', fontWeight: 700,
                    padding: '4px 8px', cursor: 'pointer', fontFamily: FONT_BODY, flexShrink: 0,
                  }}
                >
                  {t('compare.removeButton')}
                </button>
              )}
            </div>
            {rows.map(row => {
              const value = loading ? null : row.render(item);
              const empty = value === null || value === undefined || value === '';
              return (
                <div key={row.key} style={{ padding: '9px 0', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <p style={{ fontSize: '0.64rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '4px' }}>
                    {row.label}
                  </p>
                  <div style={{ fontSize: '0.84rem', color: empty ? 'rgba(255,255,255,0.3)' : WHITE, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
                    {loading ? t('common.loading') : empty ? '—' : value}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}

// Small shared value renderers so every comparison formats lists and badges the same way.
export function ChipList({ values }) {
  if (!values || values.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
      {values.map((v, i) => (
        <span key={`${v}-${i}`} style={{ fontSize: '0.74rem', color: WHITE, backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '3px 9px' }}>{v}</span>
      ))}
    </div>
  );
}

export function LineList({ values }) {
  if (!values || values.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      {values.map((v, i) => <span key={i}>{v}</span>)}
    </div>
  );
}

export function Pill({ children, color = AMBER }) {
  return (
    <span style={{ display: 'inline-block', fontSize: '0.72rem', fontWeight: 700, color, background: `${color}22`, border: `1px solid ${color}55`, borderRadius: '999px', padding: '2px 9px' }}>
      {children}
    </span>
  );
}
